import { Component, DestroyRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateCustomParserFormatter } from 'src/app/services/date-time/date-formatter-custom.service';
import { NgbStringAdapter } from 'src/app/services/date-time/ngb-date-string-adapter.service';
import {
  API,
  BudgetDTO,
  CampaignDTO,
  CampaignLevelDTO,
  CampaignTypeDTO,
  CompanyDTO,
  CompanyFullDTO,
  CullDTO,
  CullWithAtomsDTO,
  CurrencyDTO,
  GeoCullDTO,
  InventoryGeoAggregationFrontendEnhancedDTO,
  NamedIdentityDTO,
  PlayerCullDTO,
  StateSpaceCullDTO,
  TargetingDTO,
  TimeCullDTO,
  TimingRelation,
} from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { CompanyLevelEditComponent } from '../../../company/company-level-edit/company-level-edit.component';
import { UserService } from '../../../../services/user.service';
import { TranslateService } from '@ngx-translate/core';
import { CampaignTypesComponent } from 'src/app/pages/datasets/campaign-types/campaign-types.component';
import { NavigationService } from '@ngx-seatback/utils';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { iif, Observable } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { ClusterDefinition, MinMaxPoints } from '../../../../services/global/global-interfaces';
import { ToasterService, ToastType } from '../../../../services/toaster/toaster.service';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { enumKeys } from '../../../../services/global/global-methods';

@Component({
  selector: 'sb-page-campaign-edit',
  templateUrl: './campaign-edit.component.html',
  styleUrls: ['./campaign-edit.component.scss'],
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },
    { provide: NgbDateAdapter, useClass: NgbStringAdapter },
  ],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ flex: '0' }), animate('200ms 500ms linear', style({ flex: '1' }))]),
      transition(':leave', [animate('200ms 0ms linear', style({ flex: '0' }))]),
    ]),
    trigger('slideInOut', [
      transition(':enter', [style({ flex: '0' }), animate('200ms 210ms linear', style({ flex: '1' }))]),
      transition(':leave', [animate('200ms 100ms linear', style({ flex: '0' }))]),
    ]),
  ],
  standalone: false,
})
export class CampaignEditComponent implements OnInit {
  @ViewChild('filterSelection', { static: false }) eRef: ElementRef;

  minMaxPoints: MinMaxPoints;
  clusters: ClusterDefinition[];

  clientId: number;
  campaignId: number;
  levelOptions: CampaignLevelDTO[];
  campaignData: CampaignDTO;
  previewCampaignData: CampaignDTO;
  loading: boolean;
  wizardState: string;
  wizardPreviewState: string;
  customer: NamedIdentityDTO;

  timingRelations: TimingRelation[];
  isCompanyEditable: boolean;

  form = new UntypedFormGroup({
    Title: new UntypedFormControl('', [Validators.required]),
    Identifier: new UntypedFormControl(''),
    CampaignId: new UntypedFormControl(''),
    Level: new UntypedFormControl('', [Validators.required]),
  });

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    public translate: TranslateService,
    public userService: UserService,
    private titleService: Title,
    public navService: NavigationService,
    private toastHandler: ToasterService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.minMaxPoints = undefined;
    this.clusters = null;
    this.clientId = null;
    this.campaignId = null;
    this.levelOptions = [];
    this.campaignData = undefined;
    this.previewCampaignData = undefined;
    this.loading = true;
    this.wizardState = 'Map';
    this.wizardPreviewState = 'locationMap';
    this.customer = undefined;
    this.isCompanyEditable = true;
  }

  get Title() {
    return this.form.get('Title');
  }

  get Identifier() {
    return this.form.get('Identifier');
  }

  get CampaignId() {
    return this.form.get('CampaignId');
  }

  get Level() {
    return this.form.get('Level');
  }

  get campaignTypeText(): string {
    return this.campaignData?.campaignType?.name
      ? this.campaignData.campaignType.name
      : '(' + this.translate.instant('lang.globals.noCampaignType') + ')';
  }

  formBudget = new UntypedFormGroup({
    Amount: new UntypedFormControl({ value: undefined, disabled: true }, []),
    Currency: new UntypedFormControl({ value: undefined, disabled: true }, []),
    Relation: new UntypedFormControl({ value: TimingRelation.WholePeriod, disabled: true }, []),
    MaxCPM: new UntypedFormControl({ value: undefined, disabled: true }, []),
    MaxCPMDuration: new UntypedFormControl({ value: undefined, disabled: true }, []),
    MaxCPS: new UntypedFormControl({ value: undefined, disabled: true }, []),
    EnableCampaignBudgetToggle: new UntypedFormControl(false, []),
  });

  get BudgetEnableCampaignBudgetToggle() {
    return this.formBudget.get('EnableCampaignBudgetToggle');
  }

  get BudgetAmount() {
    return this.formBudget.get('Amount');
  }

  get BudgetCurrency() {
    return this.formBudget.get('Currency');
  }

  get BudgetRelation() {
    return this.formBudget.get('Relation');
  }

  get BudgetMaxCPM() {
    return this.formBudget.get('MaxCPM');
  }

  get BudgetMaxCPMDuration() {
    return this.formBudget.get('MaxCPMDuration');
  }

  get BudgetMaxCPS() {
    return this.formBudget.get('MaxCPS');
  }

  /*
         TODO outside click auf filter button click deaktivieren möglich über variable die beim button click gesetzt wird ?

   @HostListener('document:click', ['$event'])
   clickOutside(e) {
     if (this.wizardState !== 'Map') {
     if (this.eRef.nativeElement.contains(e.target)) {
     } else {
       this.changeWizardState('Map');
     }
     }
   }
   */

  ngOnInit() {
    this.initEnums();
    this.disableInput();
    this.BudgetCurrency.setValue(this.util.defaultCurrency);
    this.activatedRoute.params.subscribe(params => {
      this.clientId = params.clientID;
      this.campaignId = params.campaignID;
      this.refreshFromServer();
    });
    this.registerFormChangesListener();
    if (
      !this.permissionService.hasPermissions([
        this.permissionService.Permission.budget_create,
        this.permissionService.Permission.budget_edit,
      ])
    ) {
      this.disableBudgetForm();
      this.BudgetEnableCampaignBudgetToggle.disable();
    }
  }

  initEnums() {
    const timingKeys = enumKeys(TimingRelation);
    this.timingRelations = [];
    for (const val of timingKeys) {
      this.timingRelations.push(TimingRelation[val]);
    }
  }

  private registerFormChangesListener() {
    this.Title.valueChanges.subscribe(value => {
      if (this.campaignData) {
        this.campaignData.name = value;
      }
    });
    this.Identifier.valueChanges.subscribe(value => {
      if (this.campaignData) {
        this.campaignData.identifier = value;
      }
    });
    this.CampaignId.valueChanges.subscribe(value => {
      if (this.campaignData) {
        this.campaignData.campaignId = value;
      }
    });
    this.Level.valueChanges.subscribe((value: CampaignLevelDTO) => {
      if (value) {
        this.campaignData.level = value;
      }
    });
    this.BudgetEnableCampaignBudgetToggle.valueChanges.subscribe(value => {
      if (this.campaignData) {
        if (value) {
          if (!this.campaignData.budget) {
            this.campaignData.budget = this.api.createDTO('BudgetDTO');
            (this.campaignData.budget as unknown as any).customer = this.customer;
          }
          this.enableBudgetForm();
        } else {
          this.campaignData.budget = null;
          this.disableBudgetForm();
          this.updateForm(false);
        }
      }
    });
    this.BudgetAmount.valueChanges.subscribe(value => {
      if (this.campaignData.budget) {
        this.campaignData.budget.amount = value;
      }
    });

    this.BudgetCurrency.valueChanges.subscribe(value => {
      let realValue: CurrencyDTO = value ? JSON.parse(decodeURIComponent(value)) : value;
      if (value) {
        for (const currency of this.util.currencies) {
          if (currency.code === realValue.code && currency.name === realValue.name && currency.symbol === realValue.symbol) {
            realValue = currency;
            break;
          }
        }
      }
      this.BudgetCurrency.setValue(realValue, { emitEvent: false });

      if (this.campaignData.budget) {
        this.campaignData.budget.currency = realValue;
      }
    });
    this.BudgetMaxCPM.valueChanges.subscribe(value => {
      if (this.campaignData.budget) {
        this.campaignData.budget.maxCPM = value;
      }
    });
    this.BudgetMaxCPMDuration.valueChanges.subscribe(value => {
      if (this.campaignData.budget) {
        this.campaignData.budget.maxCPMDuration = value;
      }
    });
    this.BudgetRelation.valueChanges.subscribe(value => {
      if (this.campaignData.budget) {
        this.campaignData.budget.relation = value;
      }
    });
    this.BudgetMaxCPS.valueChanges.subscribe(value => {
      if (this.campaignData.budget) {
        this.campaignData.budget.maxCPS = value;
      }
    });
  }

  getSubmissionStatus(): boolean {
    return !(this.wizardState === 'Map' && this.budgetSubmission());
  }

  budgetSubmission(): boolean {
    if (this.BudgetEnableCampaignBudgetToggle.value === true) {
      if (this.campaignData.budget.amount) {
        if (this.campaignData.budget.maxCPMDuration) {
          return this.campaignData.budget.maxCPM !== null && this.campaignData.budget.maxCPM !== undefined;
        }
        if (this.campaignData.budget.maxCPM) {
          return false;
        }
        if (this.campaignData.budget.maxCPS) {
          return true;
        }
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  enableInput() {
    this.Level.enable();
    this.CampaignId.enable();
    this.Identifier.enable();
    this.Title.enable();
  }

  disableInput() {
    this.CampaignId.disable();
    this.Identifier.disable();
    this.Level.disable();
    this.Title.disable();
  }

  enableBudgetForm() {
    this.BudgetAmount.enable();
    this.BudgetCurrency.enable();
    this.BudgetMaxCPM.enable();
    this.BudgetMaxCPMDuration.enable();
    this.BudgetRelation.enable();
    this.BudgetMaxCPS.enable();
  }

  disableBudgetForm() {
    this.BudgetAmount.disable();
    this.BudgetCurrency.disable();
    this.BudgetMaxCPM.disable();
    this.BudgetMaxCPMDuration.disable();
    this.BudgetRelation.disable();
    this.BudgetMaxCPS.disable();
  }

  updateForm(emitEvent: boolean = true) {
    if (!this.campaignData?.id) {
      return;
    }
    this.Title.setValue(this.campaignData?.name, { emitEvent });
    this.Title.markAsTouched();
    this.Identifier.setValue(this.campaignData?.identifier, { emitEvent });
    this.Identifier.markAsTouched();
    this.CampaignId.setValue(this.campaignData?.campaignId, { emitEvent });
    this.CampaignId.markAsTouched();
    this.Level.setValue(this.findLevel(), { emitEvent });
    this.Level.markAsTouched();
    this.customer = this.campaignData.customer;

    if (this.campaignData.budget) {
      const budget = this.campaignData.budget;
      this.BudgetAmount.setValue(budget.amount, { emitEvent });
      for (const currency of this.util.currencies) {
        if (
          currency.code === budget.currency.code &&
          currency.name === budget.currency.name &&
          currency.symbol === budget.currency.symbol
        ) {
          this.BudgetCurrency.setValue(currency, { emitEvent });
          break;
        }
      }
      this.BudgetMaxCPM.setValue(budget.maxCPM, { emitEvent });
      this.BudgetMaxCPMDuration.setValue(budget.maxCPMDuration, { emitEvent });
      this.BudgetRelation.setValue(budget.relation, { emitEvent });
      this.BudgetMaxCPS.setValue(budget.maxCPS, { emitEvent });
      this.BudgetEnableCampaignBudgetToggle.setValue(true, { emitEvent });
      if (
        this.permissionService.hasPermissions([
          this.permissionService.Permission.budget_create,
          this.permissionService.Permission.budget_edit,
        ])
      ) {
        this.enableBudgetForm();
      } else {
        this.disableBudgetForm();
      }
    } else {
      this.BudgetAmount.setValue(undefined, { emitEvent });
      this.BudgetAmount.markAsUntouched();
      this.BudgetCurrency.setValue(this.util.defaultCurrency, { emitEvent });
      this.BudgetCurrency.markAsUntouched();
      this.BudgetMaxCPM.setValue(undefined, { emitEvent });
      this.BudgetMaxCPM.markAsUntouched();
      this.BudgetMaxCPMDuration.setValue(undefined, { emitEvent });
      this.BudgetMaxCPMDuration.markAsUntouched();
      this.BudgetRelation.setValue(TimingRelation.WholePeriod, { emitEvent });
      this.BudgetRelation.markAsUntouched();
      this.BudgetMaxCPS.setValue(undefined, { emitEvent });
      this.BudgetMaxCPS.markAsUntouched();
      this.BudgetEnableCampaignBudgetToggle.setValue(false, { emitEvent });
      this.disableBudgetForm();
    }

    // TODO UPDATE VALIDITY!!!  this.form.updateValueAndValidity();
  }

  findLevel() {
    for (const lvl of this.levelOptions) {
      if (lvl.name === this.campaignData.level.name && lvl.description === this.campaignData.level.description) {
        return lvl;
      }
    }
    return null;
  }

  openInventoryView() {
    this.modal
      .modal(
        {
          title: '',
          modalOptions: { windowClass: 'bigXL' },
        },
        {
          templateId: 'inventorySelectionModal',
        }
      )
      .then((cull: PlayerCullDTO) => {
        if (cull) {
          this.setCull(cull);
        }
      })
      .catch(() => {});
  }

  nextStep(wizard, nextTab) {
    wizard.selectTab(nextTab);
  }

  refreshFromServer() {
    this.loading = true;
    if (!this.campaignId) {
      this.campaignData = new CampaignDTO();
      this.campaignData.active = true;
      this.campaignData.name = '';
      this.updatePreview();
      this.preselectCompany();
      this.loading = false;
    } else {
      this.api
        .ds_cm_Campaign_retrieveById(this.campaignId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (campaignData: CampaignDTO) => {
            this.enableInput();
            this.campaignData = campaignData;
            this.customer = this.campaignData.customer;
            this.api
              .ds_cm_Campaign_retrieveLevels(this.customer.id)
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                take(1),
                finalize(() => {
                  this.loading = false;
                })
              )
              .subscribe({
                next: (campaignLevel: CampaignLevelDTO[]) => {
                  this.levelOptions = campaignLevel;
                  this.updateForm();
                  this.updatePreview();
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  createOrUpdateBudget(): Promise<void> {
    return new Promise<void>(resolve => {
      try {
        if (this.campaignData?.budget) {
          this.campaignData.budget.name = 'Budget ' + this.Title.value;
          let obs: Observable<BudgetDTO>;
          if (this.campaignData?.budget.id) {
            obs = this.api.ds_cm_Budget_editBudget(this.campaignData.budget).pipe(takeUntilDestroyed(this.destroyRef), take(1));
          } else {
            obs = this.api.ds_cm_Budget_createBudget(this.campaignData.budget).pipe(takeUntilDestroyed(this.destroyRef), take(1));
          }
          obs.subscribe({
            next: (res: BudgetDTO) => {
              this.campaignData.budget = res;
              resolve();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
        } else {
          resolve();
        }
      } catch (err) {
        resolve();
      }
    });
  }

  createAndUpdateCampaign() {
    this.loading = true;

    this.createOrUpdateBudget().then(() => {
      const type: string = this.translate.instant('lang.globals.campaign');
      iif(
        () => !this.campaignData.id,
        this.api.ds_cm_Campaign_createCampaign(this.customer.id, this.campaignData),
        this.api.ds_cm_Campaign_editCampaign(this.campaignData)
      )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: data => {
            const key = this.campaignData.id ? 'lang.globals.itemSuccessfullyEdited' : 'lang.globals.itemSuccessfullyCreated';
            this.campaignData = data;
            this.navService.navigateToPoint(RoutingID.commercials, { id: 'campaignID', value: data.id });
            this.toastHandler.toast(ToastType.SUCCESS, this.translate.instant(key, { itemName: type }));
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  selectFilter(cull: CullDTO) {
    this.setCull(cull);
    this.changeWizardState('Map');
  }

  setCull(cull: CullDTO) {
    switch (cull.type) {
      case 'TimeCull':
        this.campaignData.timeCull = cull as TimeCullDTO;
        if (!cull.id) {
          this.campaignData.timeCull = null;
        }
        break;
      case 'StateSpaceCull':
        this.campaignData.stateSpaceCull = cull as StateSpaceCullDTO;
        if (!cull.id) {
          this.campaignData.stateSpaceCull = null;
        }
        break;
      case 'GeoCull':
        this.campaignData.geoCull = cull as GeoCullDTO;
        if (!cull.id) {
          this.campaignData.geoCull = null;
        }
        break;
      case 'PlayerCull':
        this.campaignData.playerCull = cull as PlayerCullDTO;
        if (!cull.id) {
          this.campaignData.playerCull = null;
        }
        break;
    }
    this.refreshMapSettings();
    this.updatePreview();
  }

  sliceCull(e, type: string) {
    switch (type) {
      case 'TimeCull': {
        if (this.campaignData.timeCull) {
          this.campaignData.timeCull = null;
        }
        break;
      }
      case 'PlayerCull': {
        if (this.campaignData.playerCull) {
          this.campaignData.playerCull = null;
        }
        break;
      }
      case 'GeoCull': {
        if (this.campaignData.geoCull) {
          this.campaignData.geoCull = null;
        }
        break;
      }
    }
    this.updatePreview();
    this.refreshMapSettings();
    this.changeWizardState('Map');
    e.stopPropagation();
  }

  updatePreview() {
    let customerTmp = this.customer;
    if (!customerTmp) {
      customerTmp = this.userService.user.client.primaryCompany;
    }
    if (!this.campaignId && !this.campaignData.id) {
      this.api
        .ds_cm_Campaign_createCampaignPreview(customerTmp.id, this.campaignData)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (campaign: CampaignDTO) => {
            this.previewCampaignData = campaign;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ds_cm_Campaign_editCampaignPreview(this.campaignData)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (campaign: CampaignDTO) => {
            this.previewCampaignData = campaign;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  changeWizardState(state: string) {
    this.wizardState = state;
  }

  changeWizardPreviewState(state: string) {
    this.wizardPreviewState = state;
  }

  filterSubmitSuccess(cull: CullWithAtomsDTO) {
    if (!cull) {
      this.changeWizardState('Map');
    } else {
      switch (cull?.type) {
        case 'TimeCull':
          this.selectFilter(cull as TimeCullDTO);
          break;
        case 'PlayerCull':
          this.selectFilter(cull as PlayerCullDTO);
          break;
      }
    }
  }

  preselectCompany() {
    if (this.userService.user?.client?.primaryCompany && !this.campaignData.customer) {
      this.selectCompany(this.userService.user.client.primaryCompany as CompanyFullDTO);
      this.userService.selectedCompany.subscribe((company: CompanyDTO) => {
        if (company && this.isCompanyEditable) {
          this.selectCompany(company as CompanyFullDTO);
        }
      });
    }
  }

  selectCompany(company: CompanyFullDTO) {
    this.changeWizardState('Map');
    this.customer = company;
    this.enableInput();
    if (this.campaignData?.budget) {
      (this.campaignData.budget as unknown as any).customer = company;
    }
    if (this.campaignData?.targeting) {
      (this.campaignData.targeting as unknown as any).customer = company;
    }
    this.retrieveLevel();
  }

  createCompany(company: CompanyFullDTO) {
    this.changeWizardState('Map');
    this.customer = company;
    this.enableInput();
    this.retrieveLevel();
  }

  retrieveLevel() {
    this.api
      .ds_cm_Campaign_retrieveLevels(this.customer.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (level: CampaignLevelDTO[]) => {
          this.levelOptions = level;
          this.updateForm();
          this.Level.setValue(level[0]);
          this.Level.markAsTouched();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  selectCampaignType() {
    if (this.campaignData?.campaignType) {
      this.campaignData.campaignType = null;
    } else if (this.campaignData) {
      this.modal
        .showNavigationModal({
          entryComponent: { value: CampaignTypesComponent, data: {} },
          title: null,
          modalOptions: { windowClass: 'bigXL' },
        })
        .then((result: CampaignTypeDTO) => {
          if (result) {
            if (!result.id) {
              this.campaignData.campaignType = null;
            } else {
              this.campaignData.campaignType = result;
            }
          } else {
            this.campaignData.campaignType = null;
          }
        })
        .catch(() => {});
    }
  }

  sliceCompany() {
    this.customer = null;
    this.disableInput();
    this.Level.setValue(null);
  }

  showEditLevel() {
    const level = new CampaignLevelDTO();
    level.id = {};
    this.modal
      .showNavigationModal({
        entryComponent: {
          value: CompanyLevelEditComponent,
          data: { level, companyData: this.customer, levelOptions: this.levelOptions },
        },
        title: this.translate.instant('lang.companyLevel.createLevel'),
        icon: 'plus',
        modalOptions: { size: 'lg' },
      })
      .then((result: CampaignLevelDTO) => {
        this.campaignData.level = result;
        this.api
          .ds_cm_Campaign_retrieveLevels(this.customer.id)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (data: CampaignLevelDTO[]) => {
              this.levelOptions = data;
              this.Level.setValue(this.findLevel());
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      })
      .catch(() => {});
  }

  refreshMap(point?) {
    if (point) {
      this.minMaxPoints = point;
    }
    this.refreshMapSettings();
  }

  refreshMapSettings() {
    this.api
      .ds_rm_Inventory_geoAggregationFrontendEnhanced(
        99,
        {
          filters: null,
          whitelistPolygons: null,
          blacklistPolygons: null,
          playerCulls: this.getPlayerCullArray(this.campaignData?.playerCull),
          geoCulls: this.getGeoCullArray(this.campaignData?.geoCull),
          descriptorId: null,
        },
        { min: this.minMaxPoints?.min, max: this.minMaxPoints?.max }
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (aggregation: InventoryGeoAggregationFrontendEnhancedDTO) => {
          this.clusters = aggregation.data;
        },
      });
  }

  getPlayerCullArray(campaignCull: PlayerCullDTO): PlayerCullDTO[] | null {
    const res: PlayerCullDTO[] = [];
    if (!campaignCull) {
      return null;
    }
    if (campaignCull) {
      res.push(campaignCull);
    }
    return res;
  }

  getGeoCullArray(campaignCull: GeoCullDTO): GeoCullDTO[] | null {
    const res: GeoCullDTO[] = [];
    if (!campaignCull) {
      return null;
    }
    if (campaignCull) {
      res.push(campaignCull);
    }
    return res;
  }

  selectTargeting(targeting: TargetingDTO) {
    this.changeWizardState('Map');
    if (targeting) {
      this.campaignData.targeting = targeting;
    }
  }

  createTargeting() {
    if (!this.campaignData.targeting) {
      this.campaignData.targeting = this.api.createDTO('TargetingDTO');
      (this.campaignData.targeting as unknown as any).customer = this.customer;
    }
  }

  addTargeting(targeting: TargetingDTO) {
    if (targeting) {
      this.api
        .ds_cm_Targeting_createTargeting(targeting)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (createdTargeting: TargetingDTO) => {
            this.campaignData.targeting = createdTargeting;
            this.changeWizardState('Map');
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  sliceTargeting() {
    this.campaignData.targeting = null;
  }
}
